import {Injectable} from '@angular/core'
import {Router} from '@angular/router'
import {AngularFireDatabase} from 'angularfire2/database'
import {AngularFireAuth} from 'angularfire2/auth'
import {LoaderService} from '../ui-shared/loader/loader.service'
import {Subject} from 'rxjs'
import * as firebase from 'firebase'

const DEFAULT_PICTURE: string = '/assets/img/default-profile-picture.jpg'

export class User {
  uid: string
  registration_date: any

  username: string
  name: string
  email: string
  link: string
  company: string
  lang: string
  picture: string = DEFAULT_PICTURE
  last_connection: any
  provider: string
  root_folder: string

  constructor(auth) {
    this.uid = auth.uid
    this.name = auth.displayName
    this.email = auth.email
    this.picture = auth.photoURL
    this.last_connection = firebase.database.ServerValue.TIMESTAMP
    this.provider =
      auth.providerData &&
      auth.providerData[0] &&
      auth.providerData[0].providerId
  }
}

const USERCOLLECTION: string = 'users'
const USERNAMECOLLECTION: string = 'usernames'
const ADMINCOLLECTION: string = 'admin'

@Injectable()
export class UserService {
  // current: User = localStorage.getItem('currentuser') ? JSON.parse(localStorage.getItem('currentuser')) : null
  current: User
  logged_in: boolean
  admin: Subject<number> = new Subject<number>()

  default_picture = DEFAULT_PICTURE
  get: Subject<User> = new Subject<User>()
  errors: Array<any> = []

  onSignOut = new Subject()

  constructor(
    private afAuth: AngularFireAuth,
    private db: AngularFireDatabase,
    private router: Router,
    private loader: LoaderService
  ) {
    // this.afAuth.auth.signOut();
    this.afAuth.authState
      .switchMap(auth => {
        if (auth) {
          this.logged_in = true
          this.current = Object.assign(new User(auth), this.current)
          this.updateUserData()
          this.db
            .object([ADMINCOLLECTION, this.current.uid].join('/'))
            .valueChanges()
            .subscribe(admin => {
              if (admin && admin['level'] > 0) this.admin.next(admin['level'])
              else this.admin.next(0)
            })
          return this.db
            .object([USERCOLLECTION, this.current.uid].join('/'))
            .valueChanges()
        } else {
          this.logged_in = false
          this.admin.next(0)
          return []
        }
      })
      .subscribe(
        user => {
          if (!user) {
            // Create User
            // Handled in UserLogin
          } else {
            // User Exists
            this.current = user
            let folders = Array.isArray(user.folders)
              ? user.folders
              : Object.values(user.folders)
            folders = folders.filter(k => k.name !== 'demo')
            if (user.folders && folders.length == 1)
              this.current.root_folder = folders[0].id
            this.get.next(this.current)
            // this.db.collection(ADMINCOLLECTION).doc(this.current.uid).valueChanges().subscribe(useradmin => {
            //	 this.admin = useradmin && useradmin['level'] && useradmin['level'] > 0
            // }, e => console.error)
          }
        },
        error => console.error(error)
      )
  }

  getUserWithUsername(username: string) {
    let user = new Subject()
    username = username.toLowerCase()
    if (!username || username == '') return user
    let username_sub = this.db
      .object([USERNAMECOLLECTION, username].join('/'))
      .valueChanges()
      .subscribe(id => {
        username_sub.unsubscribe()
        this.db
          .object([USERCOLLECTION, id].join('/'))
          .valueChanges()
          .subscribe(_user => {
            user.next(_user)
          })
      })
    return user
  }

  getUserPicture(uid: string) {
    return this.db
      .object([USERCOLLECTION, uid, 'picture'].join('/'))
      .valueChanges()
  }

  checkUsername(username: string) {
    let user = new Subject()
    username = username.toLowerCase()
    let username_sub = this.db
      .object([USERNAMECOLLECTION, username].join('/'))
      .valueChanges()
      .subscribe(id => {
        username_sub.unsubscribe()
        user.next(id)
      })
    return user
  }

  addUserName(username: string) {
    let update = {}
    username = username.toLowerCase()
    update[username] = this.current.uid
    this.db
      .object(USERNAMECOLLECTION)
      .update(update)
      .then(data => {
        this.current.username = username
        this.create()
      })
      .catch(e => console.error(e))
  }

  edit() {
    this.router.navigate(['/user/edit', this.current.uid])
  }

  view() {
    if (this.current.username)
      this.router.navigate(['/user/view', this.current.username])
    else this.edit()
  }

  create() {
    this.db
      .object([USERCOLLECTION, this.current.uid].join('/'))
      .set(this.userAsObject())
      .then(() => {
        this.view()
      })
      .catch(e => console.error(e))
  }

  update() {
    this.db
      .object([USERCOLLECTION, this.current.uid].join('/'))
      .update(this.userAsObject())
      .then(() => {
        this.view()
      })
      .catch(e => console.error(e))
  }

  userAsObject() {
    let user_object = {}
    let keys = Object.keys(this.current)
    for (let keyindex in keys) {
      let key = keys[keyindex]
      user_object[key] = this.current[key]
    }
    return user_object
  }

  get hasUsername() {
    return this.current.username ? true : false
  }

  // Auth

  googleLogin() {
    this.loader.start()
    const provider = new firebase.auth.GoogleAuthProvider()
    return this.socialSignIn(provider)
  }

  facebookLogin() {
    this.loader.start()
    const provider = new firebase.auth.FacebookAuthProvider()
    return this.socialSignIn(provider)
  }

  emailLogin(email: string, password: string) {
    this.loader.start()
    this.afAuth.auth
      .signInWithEmailAndPassword(email, password)
      .then(() => this.updateUserData(true))
      .catch(error => {
        console.log('login')
        if (error.code == 'auth/wrong-password') {
          this.addError(error)
        } else {
          console.error(error)
          this.emailSignUp(email, password)
          this.loader.update('Creating user...')
          // console.log('User does not exist. Try to create')
        }
      })
  }

  emailSignUp(email: string, password: string) {
    this.afAuth.auth
      .createUserWithEmailAndPassword(email, password)
      .then(() => this.updateUserData(true))
      .catch(error => this.addError(error))
  }

  addError(error) {
    this.loader.stop()
    console.error(error)
    this.errors.push(error)
  }

  cleanErrors() {
    this.errors = []
  }

  signOut(): void {
    this.afAuth.auth.signOut().then(() => {
      this.current = null
      this.logged_in = false
      this.admin.next(0)
      this.onSignOut.next()
      this.router.navigate(['/'])
    })
  }

  private socialSignIn(provider) {
    // let navvar: any;
    // navvar = window.navigator;
    // if (('standalone' in navvar) && navvar.standalone) {
    // 	return this.afAuth.auth.signInWithRedirect(provider)
    // 	.then((credential) => {
    // 		this.updateUserData(true)
    // 	})
    // 	.catch(error => console.log(error))
    // } else {
    return this.afAuth.auth
      .signInWithPopup(provider)
      .then(credential => {
        this.updateUserData(true)
      })
      .catch(error => console.log(error))
    // }
  }

  private updateUserData(full: boolean = false) {
    this.loader.stop()
    if (!this.current || !this.current.username) {
      let sub = this.get.subscribe(user => {
        sub.unsubscribe()
        this.updateUserData(full)
      })
      return
    }

    let update
    if (full) {
      update = this.userAsObject()
      update['registration_date'] =
        update['registration_date'] || firebase.database.ServerValue.TIMESTAMP
    } else {
      update = {
        last_connection: firebase.database.ServerValue.TIMESTAMP,
      }
    }

    // localStorage.setItem('currentuser', JSON.stringify(this.current))
    return this.db
      .object([USERCOLLECTION, this.current.uid].join('/'))
      .update(update)
      .then(() => {
        if (!this.current.username) this.edit()
        else if (full) this.router.navigate(['dashboard'])
      })
  }
}



// WEBPACK FOOTER //
// /Users/axel/Sites/Bannerboy/Internal/attaboy-angular/src/app/user/user.service.ts